import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UnitMappingByIssuerAndIdentifierPayload,
  type UnitMappingByIssuerAndIdentifierResponse,
} from './types';
import { zodUnitMappingByIdentifierAndIssuerSearchParamsSchema } from './validations';

/**
 * Fetches a unit mapping by issuer and identifier from the API.
 * @param unitMappingContext - The context (issuer and unit identifier) of the unit mapping to fetch.
 * @returns The mapped UN/CEFACT unit.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_unit_mapping_for_identifier_and_issuer_units_unit_mapping_query_get
 */
export const fetchUnitMappingByIdentifierAndIssuer = async ({
  issuerId,
  unitIdentifier,
}: UnitMappingByIssuerAndIdentifierPayload) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.UNITS.GET_MAPPING_BY_ISSUER_AND_IDENTIFIER(), {
        searchParams: {
          issuerId,
          unitIdentifier,
        },
        validate: {
          searchParams: zodUnitMappingByIdentifierAndIssuerSearchParamsSchema,
        },
      })
      .json<UnitMappingByIssuerAndIdentifierResponse>();

    return response;
  } catch (error) {
    if (error?.response?.status === 404) {
      // No need to handle the 404 error if there just is no mapping yet
      return '';
    }

    log.error({
      message: 'Error fetching unit mapping by issuer and identifier',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.unit.fetchUnitMappingByIdentifierAndIssuer.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.unit.fetchUnitMappingByIdentifierAndIssuer.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
